/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * DocBits - API
 * DocBits - API
 * OpenAPI spec version: 11.97.2
 */
import type {
  BodyCreateUserUsersCreatePost,
  BodyUpdateUserUsersUpdateUserIdPut,
  CheckUserOnlinesUsersCheckUserOnlineGetParams,
  CreateUserUsersCreatePost200,
  DeleteUserUsersDeleteUserIdDeleteParams,
  GetUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGetParams,
  GetUsersUsersGetUsersGetParams,
  GetUsersWhoCanApproveUsersGetUsersWhoCanApproveGetParams,
} from '.././models';

import { customAxiosInstance } from '../../mutator/custom-axios-instance';

export const getUsers = () => {
  /**
 * **Overview:**
Lists all users within an organization with support for keyword search and dynamic sorting.
For supplier users, returns only suppliers users within their org. Includes role information
(admin status, system user flag) for each user.

**Use Cases:**
1. **User Management Dashboard** - Display paginated user list with search
2. **User Administration** - Find users by name or email for management operations
3. **Permission Assignment** - List users before assigning roles or groups
4. **Access Audit** - Get list of users with their roles and admin status
5. **Bulk Operations** - Get users matching criteria for batch actions
6. **Supplier Portal** - Show users in supplier organization

**Related Endpoints:**
- GET /get_user_with_groups_and_sub_organisations_sync - Get detailed user profile
- POST /create - Create new user
- PUT /update/{user_id} - Update user details
- DELETE /delete/{user_id} - Delete user
- GET /get_users_who_can_approve - Get approval authority users

**Performance Notes:**
- Typical response time: 50-200ms
- JOIN query on users_cache and user_organisation_cache
- Search filters applied on full name and email (case-insensitive)
- Scales linearly with number of users (100+ users: ~150-300ms)

**Side Effects:**
- Triggers cache synchronization (cache_syncer.sync_all_tables())
- Supplier users get filtered to their supplier org
 * @summary List organization users with filtering and sorting
 */
  const getUsersUsersGetUsersGet = (params: GetUsersUsersGetUsersGetParams) => {
    return customAxiosInstance<unknown>({
      url: `/users/get_users`,
      method: 'GET',
      params,
    });
  };
  /**
 * **Overview:**
Retrieves comprehensive user details including associated groups, sub-organizations, and role information.
Combines user profile data with group membership and organizational hierarchy. For supplier users,
returns data for their supplier organization instead of the requested organization.

**Use Cases:**
1. **User Profile Page** - Display user details with their groups and assigned organizations
2. **Permission Verification** - Check user's group membership and org hierarchy
3. **User Administration** - Edit user with pre-loaded group and organization data
4. **Access Control** - Verify user's sub-organization assignments
5. **User Audit** - Track which groups and organizations a user is assigned to
6. **Multi-Org Management** - Show user's visibility across multiple sub-organizations

**Related Endpoints:**
- GET /get_users - List all users in organization
- POST /create - Create new user
- PUT /update/{user_id} - Update user details
- DELETE /delete/{user_id} - Delete user

**Performance Notes:**
- Typical response time: 100-250ms
- Makes 3 database queries (user details, group memberships, sub-organizations)
- Small latency for group/org data retrieval
- Scales well with 50+ organizations

**Side Effects:**
- No cache synchronization on read (read-only operation)
- Supplier org_id conversion if user is supplier
- Validation of user-organization relationship
 * @summary Get user details with groups and sub-organizations
 */
  const getUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGet = (
    params: GetUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGetParams,
  ) => {
    return customAxiosInstance<unknown>({
      url: `/users/get_user_with_groups_and_sub_organisations_sync`,
      method: 'GET',
      params,
    });
  };
  /**
 * **Overview:**
Creates a new user account in the specified organization. Supports both JSON and Form data formats.
Validates password strength (8-20 chars, mixed case, numbers, special chars) and email format.
Automatically syncs cache after user creation. For supplier users, creates user in supplier org.

**Use Cases:**
1. **User Onboarding** - Add new team member to organization
2. **Admin Panel** - Create user with admin privileges
3. **Bulk User Creation** - Programmatic user account setup
4. **Self-Service Registration** - Guided user account creation
5. **API Integration** - Automated account provisioning from HR systems
6. **Multi-Role Assignment** - Create users with system user flag

**Related Endpoints:**
- GET /get_users - List users in organization
- GET /get_user_with_groups_and_sub_organisations_sync - Get user details
- PUT /update/{user_id} - Update user information
- DELETE /delete/{user_id} - Delete user account

**Performance Notes:**
- Typical response time: 200-500ms
- Auth service call for user creation (~300ms)
- Cache sync operation on completion (~100-200ms)
- Email validation: Regex pattern check (~1ms)
- Scales well for batch operations with retry logic

**Side Effects:**
- Forces cache synchronization (cache_syncer.force_sync_all_tables())
- Creates user in auth service
- Updates users_cache and user_organisation_cache tables
- Sends welcome email (if configured)
- Logs user creation event
 * @summary Create a new user in the organization
 */
  const createUserUsersCreatePost = (
    bodyCreateUserUsersCreatePost: BodyCreateUserUsersCreatePost,
  ) => {
    const formUrlEncoded = new URLSearchParams();
    if (bodyCreateUserUsersCreatePost.org_id !== undefined) {
      formUrlEncoded.append(`org_id`, bodyCreateUserUsersCreatePost.org_id);
    }
    if (bodyCreateUserUsersCreatePost.first_name !== undefined) {
      formUrlEncoded.append(
        `first_name`,
        bodyCreateUserUsersCreatePost.first_name,
      );
    }
    if (bodyCreateUserUsersCreatePost.last_name !== undefined) {
      formUrlEncoded.append(
        `last_name`,
        bodyCreateUserUsersCreatePost.last_name,
      );
    }
    if (bodyCreateUserUsersCreatePost.email !== undefined) {
      formUrlEncoded.append(`email`, bodyCreateUserUsersCreatePost.email);
    }
    if (bodyCreateUserUsersCreatePost.password !== undefined) {
      formUrlEncoded.append(`password`, bodyCreateUserUsersCreatePost.password);
    }
    if (bodyCreateUserUsersCreatePost.is_admin !== undefined) {
      formUrlEncoded.append(
        `is_admin`,
        bodyCreateUserUsersCreatePost.is_admin.toString(),
      );
    }
    if (bodyCreateUserUsersCreatePost.is_system_user !== undefined) {
      formUrlEncoded.append(
        `is_system_user`,
        bodyCreateUserUsersCreatePost.is_system_user.toString(),
      );
    }

    return customAxiosInstance<CreateUserUsersCreatePost200>({
      url: `/users/create`,
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      data: formUrlEncoded,
    });
  };
  /**
   * Get the current authenticated user's profile information including name, email, organization, and role.
   * @summary Get authenticated user's profile
   */
  const getUserProfileUsersProfileGet = () => {
    return customAxiosInstance<unknown>({
      url: `/users/profile`,
      method: 'GET',
    });
  };
  /**
 * **Overview:**
Deletes a user account from the specified organization. Performs soft delete marking user as inactive.
Validates that user belongs to organization before deletion. For supplier users, deletes from supplier org.
Logs deletion event and triggers cache synchronization.

**Use Cases:**
1. **User Offboarding** - Remove employee from organization
2. **Account Management** - Delete inactive or duplicate accounts
3. **Admin Panel** - Remove user via UI
4. **API Integration** - Automated account removal from HR systems
5. **Data Cleanup** - Remove test or invalid user accounts
6. **Access Revocation** - Immediately revoke user access

**Related Endpoints:**
- GET /get_users - List organization users
- GET /get_user_with_groups_and_sub_organisations_sync - Get user details
- POST /create - Create new user
- PUT /update/{user_id} - Update user information

**Performance Notes:**
- Typical response time: 150-400ms
- Auth service call for deletion (~250ms)
- Cache sync operation (~100-200ms)
- Permission check: O(1) lookup

**Side Effects:**
- Forces cache synchronization (cache_syncer.force_sync_all_tables())
- Deletes user from auth service
- Marks user as deleted in cache tables (soft delete)
- Revokes all active sessions/tokens
- Removes user from all groups
- Logs deletion event with audit trail
 * @summary Delete a user from the organization
 */
  const deleteUserUsersDeleteUserIdDelete = (
    userId: number,
    params?: DeleteUserUsersDeleteUserIdDeleteParams,
  ) => {
    return customAxiosInstance<unknown>({
      url: `/users/delete/${userId}`,
      method: 'DELETE',
      params,
    });
  };
  /**
 * **Overview:**
Updates user account information including profile details and role assignments. Supports both JSON
and Form data formats. Validates changes and synchronizes cache. For supplier users, updates user in
supplier org. Handles boolean string conversion for admin flags.

**Use Cases:**
1. **Profile Updates** - Change name, email, or password
2. **Role Changes** - Promote/demote user to/from admin
3. **User Administration** - Bulk updates via API
4. **Self-Service** - User updates own profile
5. **Permission Management** - Enable/disable system user flag
6. **Account Recovery** - Reset password for user

**Related Endpoints:**
- GET /get_users - List organization users
- GET /get_user_with_groups_and_sub_organisations_sync - Get user details
- POST /create - Create new user
- DELETE /delete/{user_id} - Delete user account

**Performance Notes:**
- Typical response time: 200-500ms
- Auth service call for update (~300ms)
- Cache sync operation (~100-200ms)
- Email uniqueness check (if email changed)
- Scales well for single or batch updates

**Side Effects:**
- Forces cache synchronization (cache_syncer.force_sync_all_tables())
- Updates user in auth service
- Modifies users_cache and user_organisation_cache tables
- Triggers password reset if changed
- Logs update event with audit trail
- May revoke sessions if role changes
 * @summary Update user profile and role information
 */
  const updateUserUsersUpdateUserIdPut = (
    userId: number,
    bodyUpdateUserUsersUpdateUserIdPut: BodyUpdateUserUsersUpdateUserIdPut,
  ) => {
    const formUrlEncoded = new URLSearchParams();
    if (bodyUpdateUserUsersUpdateUserIdPut.org_id !== undefined) {
      formUrlEncoded.append(
        `org_id`,
        bodyUpdateUserUsersUpdateUserIdPut.org_id,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.first_name !== undefined) {
      formUrlEncoded.append(
        `first_name`,
        bodyUpdateUserUsersUpdateUserIdPut.first_name,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.last_name !== undefined) {
      formUrlEncoded.append(
        `last_name`,
        bodyUpdateUserUsersUpdateUserIdPut.last_name,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.email !== undefined) {
      formUrlEncoded.append(`email`, bodyUpdateUserUsersUpdateUserIdPut.email);
    }
    if (
      bodyUpdateUserUsersUpdateUserIdPut.is_organisation_admin !== undefined
    ) {
      formUrlEncoded.append(
        `is_organisation_admin`,
        bodyUpdateUserUsersUpdateUserIdPut.is_organisation_admin,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.is_system_user !== undefined) {
      formUrlEncoded.append(
        `is_system_user`,
        bodyUpdateUserUsersUpdateUserIdPut.is_system_user,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.is_active !== undefined) {
      formUrlEncoded.append(
        `is_active`,
        bodyUpdateUserUsersUpdateUserIdPut.is_active,
      );
    }
    if (bodyUpdateUserUsersUpdateUserIdPut.password !== undefined) {
      formUrlEncoded.append(
        `password`,
        bodyUpdateUserUsersUpdateUserIdPut.password,
      );
    }

    return customAxiosInstance<unknown>({
      url: `/users/update/${userId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      data: formUrlEncoded,
    });
  };
  /**
 * **Overview:**
Retrieves list of users in the organization who have approval authority for a specific document type.
Supports filtering by approval level (first-level, second-level, or all). Returns only users with
required group permissions and organization access. Results cached for performance.

**Use Cases:**
1. **Approval Workflows** - Find approvers for document submission
2. **Permission Assignment** - Show eligible approvers when creating approvals
3. **Audit Trail** - Track who can approve specific document types
4. **Escalation Rules** - Determine escalation targets
5. **Approval Delegation** - Find users to delegate approval authority
6. **Document Routing** - Auto-route to appropriate approvers

**Related Endpoints:**
- GET /get_users - List organization users
- GET /get_user_with_groups_and_sub_organisations_sync - Get user details
- POST /create - Create new user
- PUT /update/{user_id} - Update user information

**Performance Notes:**
- Typical response time: 50-150ms
- Cached query with group-based filtering
- Uses permission system (ugp module)
- Scales well with 100+ users

**Side Effects:**
- No cache synchronization (read-only operation)
- Accesses user_and_group_permissions system
- Filters by organization context
 * @summary Get users authorized to approve document types
 */
  const getUsersWhoCanApproveUsersGetUsersWhoCanApproveGet = (
    params: GetUsersWhoCanApproveUsersGetUsersWhoCanApproveGetParams,
  ) => {
    return customAxiosInstance<unknown>({
      url: `/users/get_users_who_can_approve`,
      method: 'GET',
      params,
    });
  };
  /**
 * **Overview:**
Validates that user is online and refreshes the user's cache entry in Redis. Sets a TTL-based cache
marker indicating user activity. Used for presence detection, session validation, and real-time
user activity tracking. Simple heartbeat mechanism for user status.

**Use Cases:**
1. **User Presence Detection** - Check if user is actively using the application
2. **Session Validation** - Confirm user session is still active
3. **Activity Tracking** - Update last seen timestamp
4. **Real-Time Features** - Know which users are currently online
5. **Heartbeat Mechanism** - Client periodic ping to keep session alive
6. **UI Status Indicators** - Show online/offline status in UI

**Related Endpoints:**
- GET /get_users - List organization users
- GET /get_user_with_groups_and_sub_organisations_sync - Get user details
- POST /create - Create new user
- GET /check_user_online - Check user online status

**Performance Notes:**
- Typical response time: 10-50ms
- Redis SET operation (~5-10ms)
- No database queries
- TTL expiration: 60 seconds
- Scales linearly with active users

**Side Effects:**
- Creates/updates Redis cache entry
- Sets TTL of 60 seconds on cache key
- Key format: "REFRESH_API_CACHE:<org_id>"
- Triggers cache refresh in connected services
- No database modifications
 * @summary Check user online status and refresh user cache
 */
  const checkUserOnlinesUsersCheckUserOnlineGet = (
    params: CheckUserOnlinesUsersCheckUserOnlineGetParams,
  ) => {
    return customAxiosInstance<unknown>({
      url: `/users/check_user_online`,
      method: 'GET',
      params,
    });
  };
  return {
    getUsersUsersGetUsersGet,
    getUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGet,
    createUserUsersCreatePost,
    getUserProfileUsersProfileGet,
    deleteUserUsersDeleteUserIdDelete,
    updateUserUsersUpdateUserIdPut,
    getUsersWhoCanApproveUsersGetUsersWhoCanApproveGet,
    checkUserOnlinesUsersCheckUserOnlineGet,
  };
};
export type GetUsersUsersGetUsersGetResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getUsers>['getUsersUsersGetUsersGet']>>
>;
export type GetUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGetResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<
          typeof getUsers
        >['getUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGet']
      >
    >
  >;
export type CreateUserUsersCreatePostResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getUsers>['createUserUsersCreatePost']>>
>;
export type GetUserProfileUsersProfileGetResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUsers>['getUserProfileUsersProfileGet']>
  >
>;
export type DeleteUserUsersDeleteUserIdDeleteResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUsers>['deleteUserUsersDeleteUserIdDelete']>
  >
>;
export type UpdateUserUsersUpdateUserIdPutResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUsers>['updateUserUsersUpdateUserIdPut']>
  >
>;
export type GetUsersWhoCanApproveUsersGetUsersWhoCanApproveGetResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<
          typeof getUsers
        >['getUsersWhoCanApproveUsersGetUsersWhoCanApproveGet']
      >
    >
  >;
export type CheckUserOnlinesUsersCheckUserOnlineGetResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUsers>['checkUserOnlinesUsersCheckUserOnlineGet']
    >
  >
>;
